import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  Grid,
  Link,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditAccessIcon,
} from '@mui/icons-material';
import { formatTime } from '../utils/formatters';
import api from '../services/api';

/**
 * AppointmentFormDialog - Modal auto-suficiente para criação de agendamentos
 * 
 * Carrega seus próprios dados via API ao abrir, independente da página onde está.
 * Pode ser usado em qualquer lugar sem necessidade de preparação prévia.
 * 
 * @param {Object} props
 * @param {boolean} props.open - Se o dialog está aberto
 * @param {Function} props.onClose - Callback ao fechar
 * @param {Function} props.onSuccess - Callback ao criar agendamento com sucesso
 * @param {Object} props.prefillData - Dados do cliente (nome, telefone, email)
 * @param {Object} props.initialData - Dados para reagendamento
 * @param {Date} props.preselectedDate - Data pré-selecionada
 * @param {Object} props.prefillConfig - Configuração de pré-preenchimento dinâmico
 * @param {string} props.prefillConfig.professionalId - ID do profissional a pré-selecionar
 * @param {string} props.prefillConfig.serviceId - ID do serviço a pré-selecionar
 * @param {string} props.prefillConfig.placeId - ID do local a pré-selecionar
 * @param {Array} props.prefillConfig.nextSlots - Próximos slots disponíveis (clicáveis)
 */
export default function AppointmentFormDialog({ 
  open, 
  onClose, 
  onSuccess,
  prefillData = null,
  initialData = null,
  preselectedDate = null,
  prefillConfig = null,
}) {
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Dados carregados via API (auto-suficiente)
  const [professionals, setProfessionals] = useState([]);
  const [services, setServices] = useState([]);
  const [places, setPlaces] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    professionalId: '',
    serviceId: '',
    placeId: '',
    date: preselectedDate 
      ? preselectedDate.toISOString().split('T')[0] 
      : new Date().toISOString().split('T')[0],
    selectedSlot: null,
    clientName: initialData?.clientName || prefillData?.clientName || '',
    clientPhone: initialData?.clientPhone || prefillData?.clientPhone || '',
    clientEmail: initialData?.clientEmail || prefillData?.clientEmail || '',
    clientNotes: initialData?.clientNotes || ''
  });
  
  // Próximos slots disponíveis (buscados automaticamente quando prof+service+place selecionados)
  const [nextAvailableSlots, setNextAvailableSlots] = useState([]);
  const [loadingNextSlots, setLoadingNextSlots] = useState(false);
  
  // Available slots do dia selecionado
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Filtered data
  const [filteredServices, setFilteredServices] = useState([]);
  const [filteredPlaces, setFilteredPlaces] = useState([]);
  const [filteredProfessionals, setFilteredProfessionals] = useState([]);

  // Verificar se há dados suficientes para agendar
  const hasNoData = useMemo(() => {
    return professionals.length === 0 || places.length === 0;
  }, [professionals, places]);

  // Verificar se um serviço específico tem profissionais que o atendem
  const serviceHasAvailableProfessionals = useCallback((serviceId) => {
    return places.some(place => 
      (place.professionals || []).some(pp => 
        pp.serviceIds?.includes(serviceId)
      )
    );
  }, [places]);

  // Carregar dados via API e aplicar pré-preenchimento quando o dialog abre
  useEffect(() => {
    if (!open) return;
    
    console.log('[AppointmentFormDialog] Dialog aberto com prefillConfig:', prefillConfig);
    
    // Reset estado
    setDataLoaded(false);
    setError(null);
    setNextAvailableSlots([]);
    setLoadingNextSlots(false);
    
    // Carregar dados via API
    const loadFormData = async () => {
      try {
        setLoadingData(true);
        console.log('[AppointmentFormDialog] Carregando dados do formulário via API...');
        
        const response = await api.get('/appointments/form-data');
        const data = response.data || {};
        
        setProfessionals(data.professionals || []);
        setServices(data.services || []);
        setPlaces(data.places || []);
        
        console.log('[AppointmentFormDialog] Dados carregados:', {
          professionals: data.professionals?.length,
          services: data.services?.length,
          places: data.places?.length
        });
        
        // Aplicar pré-preenchimento após dados carregarem
        const newFormData = {
          professionalId: '',
          serviceId: '',
          placeId: '',
          date: preselectedDate 
            ? preselectedDate.toISOString().split('T')[0] 
            : new Date().toISOString().split('T')[0],
          selectedSlot: null,
          clientName: initialData?.clientName || prefillData?.clientName || '',
          clientPhone: initialData?.clientPhone || prefillData?.clientPhone || '',
          clientEmail: initialData?.clientEmail || prefillData?.clientEmail || '',
          clientNotes: initialData?.clientNotes || ''
        };
        
        if (prefillConfig) {
          // Aplicar dados pré-preenchidos validando se existem
          if (prefillConfig.professionalId) {
            const profExists = (data.professionals || []).find(p => p.id === prefillConfig.professionalId);
            if (profExists) {
              newFormData.professionalId = prefillConfig.professionalId;
            } else {
              console.warn('[AppointmentFormDialog] Profissional não encontrado:', prefillConfig.professionalId);
            }
          }
          
          if (prefillConfig.placeId) {
            const placeExists = (data.places || []).find(p => p.id === prefillConfig.placeId);
            if (placeExists) {
              newFormData.placeId = prefillConfig.placeId;
            } else {
              console.warn('[AppointmentFormDialog] Local não encontrado:', prefillConfig.placeId);
            }
          }
          
          if (prefillConfig.serviceId) {
            const serviceExists = (data.services || []).find(s => s.id === prefillConfig.serviceId);
            if (serviceExists) {
              // Verificar se o serviço tem profissionais disponíveis
              const hasProfs = (data.places || []).some(place => 
                (place.professionals || []).some(pp => 
                  pp.serviceIds?.includes(prefillConfig.serviceId)
                )
              );
              if (!hasProfs) {
                setError(`O serviço "${serviceExists.name}" não tem profissionais disponíveis em nenhum local. Configure um local com profissionais que atendam este serviço.`);
              } else {
                newFormData.serviceId = prefillConfig.serviceId;
              }
            } else {
              console.warn('[AppointmentFormDialog] Serviço não encontrado:', prefillConfig.serviceId);
            }
          }
        }
        
        setFormData(newFormData);
        setDataLoaded(true);
      } catch (err) {
        console.error('[AppointmentFormDialog] Erro ao carregar dados:', err);
        setError('Erro ao carregar dados do formulário. Tente novamente.');
      } finally {
        setLoadingData(false);
      }
    };
    
    loadFormData();
  }, [open, prefillConfig, preselectedDate, initialData, prefillData]);

  // Filtrar serviços baseado no profissional selecionado
  useEffect(() => {
    if (!formData.professionalId) {
      setFilteredServices([]);
      return;
    }
    
    // Buscar todos os places onde o profissional atende
    const professionalLinks = places.flatMap(place => 
      (place.professionals || []).filter(pp => pp.professionalId === formData.professionalId)
    );

    // Extrair serviceIds únicos dos vínculos
    const serviceIds = new Set();
    professionalLinks.forEach(pp => {
      if (pp.serviceIds && Array.isArray(pp.serviceIds)) {
        pp.serviceIds.forEach(id => serviceIds.add(id));
      }
    });

    // Filtrar serviços
    const filtered = services.filter(s => serviceIds.has(s.id));
    setFilteredServices(filtered);
  }, [formData.professionalId, places, services]);

  // Não precisamos mais filtrar profissionais (sempre mostra todos)
  useEffect(() => {
    setFilteredProfessionals(professionals);
  }, [professionals]);

  // Filtrar locais baseado no profissional e serviço
  useEffect(() => {
    if (!formData.professionalId || !formData.serviceId) {
      setFilteredPlaces([]);
      return;
    }
    
    // Buscar places onde o profissional atende E oferece o serviço selecionado
    const validPlaces = places.filter(place => {
      const professionalLink = (place.professionals || []).find(
        pp => pp.professionalId === formData.professionalId &&
              pp.serviceIds?.includes(formData.serviceId)
      );
      return professionalLink !== undefined;
    });

    setFilteredPlaces(validPlaces);
  }, [formData.professionalId, formData.serviceId, places]);

  // Atualizar data quando preselectedDate mudar
  useEffect(() => {
    if (preselectedDate) {
      setFormData(prev => ({
        ...prev,
        date: preselectedDate.toISOString().split('T')[0]
      }));
    }
  }, [preselectedDate]);

  // Buscar próximos slots disponíveis quando profissional + serviço + local estiverem selecionados
  useEffect(() => {
    if (!formData.professionalId || !formData.serviceId || !formData.placeId) {
      setNextAvailableSlots([]);
      return;
    }

    const loadNextSlots = async () => {
      try {
        setLoadingNextSlots(true);
        const response = await api.get('/appointments/next-available-slots', {
          params: {
            professionalId: formData.professionalId,
            serviceId: formData.serviceId,
            placeId: formData.placeId,
            count: 5
          }
        });
        setNextAvailableSlots(response.data || []);
      } catch (err) {
        console.error('Erro ao buscar próximos horários:', err);
        setNextAvailableSlots([]);
      } finally {
        setLoadingNextSlots(false);
      }
    };

    loadNextSlots();
  }, [formData.professionalId, formData.serviceId, formData.placeId]);

  // Parâmetro slotToSelect: slot para selecionar automaticamente após carregar (passado diretamente para evitar problemas de estado assíncrono)
  const loadAvailableSlots = async (slotToSelect = null) => {
    try {
      setLoadingSlots(true);
      setError(null);

      const response = await api.get('/appointments/available-slots', {
        params: {
          professionalId: formData.professionalId,
          serviceId: formData.serviceId,
          placeId: formData.placeId,
          date: formData.date
        }
      });

      const slots = response.data || [];
      setAvailableSlots(slots);
      
      // Se foi passado um slot para selecionar, buscar e selecionar
      if (slotToSelect) {
        const matchingSlot = slots.find(
          s => new Date(s.startTime).getTime() === new Date(slotToSelect.startTime).getTime()
        );
        if (matchingSlot) {
          setFormData(prev => ({ ...prev, selectedSlot: matchingSlot }));
        }
      } else if (formData.selectedSlot) {
        // Reset selected slot se não estiver mais disponível
        const slotStillAvailable = slots.some(
          s => new Date(s.startTime).getTime() === new Date(formData.selectedSlot.startTime).getTime()
        );
        if (!slotStillAvailable) {
          setFormData(prev => ({ ...prev, selectedSlot: null }));
        }
      }
    } catch (err) {
      console.error('Erro ao carregar horários disponíveis:', err);
      setError('Erro ao carregar horários disponíveis');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleChange = (field, value) => {
    // Reset campos dependentes de forma simples (profissional → serviço → local)
    if (field === 'professionalId') {
      setFormData(prev => ({ 
        ...prev, 
        professionalId: value,
        serviceId: '', 
        placeId: '', 
        selectedSlot: null 
      }));
      setAvailableSlots([]);
    } else if (field === 'serviceId') {
      setFormData(prev => ({ 
        ...prev, 
        serviceId: value,
        placeId: '', 
        selectedSlot: null 
      }));
      setAvailableSlots([]);
    } else if (field === 'placeId' || field === 'date') {
      setFormData(prev => ({ ...prev, [field]: value, selectedSlot: null }));
      setAvailableSlots([]);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Handler para clicar em um slot dos próximos disponíveis
  // Define a data e carrega horários do dia, passando o slot para seleção automática
  const handleNextSlotClick = (slot) => {
    const slotDate = new Date(slot.startTime);
    const newDate = slotDate.toISOString().split('T')[0];
    
    // Atualizar data primeiro
    setFormData(prev => ({
      ...prev,
      date: newDate,
      selectedSlot: null
    }));
    
    // Buscar horários do dia passando o slot diretamente para seleção
    // Usar setTimeout para garantir que o state foi atualizado
    setTimeout(async () => {
      try {
        setLoadingSlots(true);
        setError(null);

        const response = await api.get('/appointments/available-slots', {
          params: {
            professionalId: formData.professionalId,
            serviceId: formData.serviceId,
            placeId: formData.placeId,
            date: newDate // Usar a data diretamente, não do state
          }
        });

        const slots = response.data || [];
        setAvailableSlots(slots);
        
        // Buscar o slot correspondente e selecionar
        const matchingSlot = slots.find(
          s => new Date(s.startTime).getTime() === new Date(slot.startTime).getTime()
        );
        if (matchingSlot) {
          setFormData(prev => ({ ...prev, selectedSlot: matchingSlot }));
        }
      } catch (err) {
        console.error('Erro ao carregar horários disponíveis:', err);
        setError('Erro ao carregar horários disponíveis');
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    }, 50);
  };

  const handleSubmit = async () => {
    if (!formData.selectedSlot) {
      setError('Selecione um horário disponível');
      return;
    }

    if (!formData.clientName.trim()) {
      setError('Nome do cliente é obrigatório');
      return;
    }

    if (!formData.clientPhone.trim()) {
      setError('Telefone do cliente é obrigatório');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await api.post('/appointments', {
        professionalId: formData.professionalId,
        placeId: formData.placeId,
        scheduleId: formData.selectedSlot.scheduleId,
        serviceId: formData.serviceId,
        startTime: formData.selectedSlot.startTime,
        clientName: formData.clientName,
        clientPhone: formData.clientPhone,
        clientEmail: formData.clientEmail || null,
        clientNotes: formData.clientNotes || null
      });

      onSuccess();
    } catch (err) {
      console.error('Erro ao criar agendamento:', err);
      setError(err.response?.data?.message || 'Erro ao criar agendamento');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !loadingData) {
      setFormData({
        professionalId: '',
        serviceId: '',
        placeId: '',
        date: new Date().toISOString().split('T')[0],
        selectedSlot: null,
        clientName: '',
        clientPhone: '',
        clientEmail: '',
        clientNotes: ''
      });
      // Limpar dados carregados via API
      setProfessionals([]);
      setServices([]);
      setPlaces([]);
      setAvailableSlots([]);
      setNextAvailableSlots([]);
      setFilteredServices([]);
      setFilteredPlaces([]);
      setFilteredProfessionals([]);
      setError(null);
      setDataLoaded(false);
      onClose();
    }
  };

  const formatSlotTime = (slot) => {
    return formatTime(slot.startTime);
  };

  const formatSlotDateTime = (slot) => {
    const date = new Date(slot.startTime);
    return date.toLocaleDateString('pt-BR', { 
      weekday: 'short', 
      day: '2-digit', 
      month: '2-digit',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Verifica se o profissional selecionado permite criar agendamentos
  // Owner tem acesso total; compartilhado precisa de ReadWrite (2) em Appointments (entityType=2)
  const canScheduleProfessional = (prof) => {
    if (!prof) return true;
    if (!prof.isShared) return true;
    return (prof.permissions?.[2] ?? 0) >= 2;
  };

  const selectedProfessional = professionals.find(p => p.id === formData.professionalId);
  const selectedProfCanSchedule = canScheduleProfessional(selectedProfessional);
  const canSubmit = formData.selectedSlot && formData.clientName.trim() && formData.clientPhone.trim() && !loading && selectedProfCanSchedule;

  // Sempre mostrar todos os campos - prefillConfig apenas pré-seleciona valores
  // O usuário pode alterar qualquer seleção se desejar

  // Info de pré-preenchimento removida - agora usamos apenas dropdowns editáveis

  // Renderizar campos de seleção - sempre na mesma ordem: profissional → serviço → local
  // Prefill apenas pré-seleciona valores, todos os campos são editáveis
  const renderSelectionFields = () => {
    return (
      <>
        {/* Step 1: Professional */}
        <TextField
          select
          fullWidth
          label="Profissional"
          value={formData.professionalId}
          onChange={(e) => handleChange('professionalId', e.target.value)}
          disabled={loading}
          sx={{ mb: 2 }}
        >
          <MenuItem value=""><em>Selecione um profissional</em></MenuItem>
          {professionals.map((prof) => {
            const canSchedule = canScheduleProfessional(prof);
            const showBadge = prof.isShared;
            const hasWrite = (prof.permissions?.[2] ?? 0) >= 2;
            return (
              <MenuItem key={prof.id} value={prof.id} disabled={!canSchedule}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <span>{prof.name}{prof.specialty && ` - ${prof.specialty}`}</span>
                  {showBadge && (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 'auto', pl: 1 }}>
                      {hasWrite
                        ? <EditAccessIcon sx={{ fontSize: 15, color: 'success.main' }} titleAccess="Permissão de edição" />
                        : <VisibilityIcon sx={{ fontSize: 15, color: 'text.secondary' }} titleAccess="Somente visualização" />}
                    </Box>
                  )}
                </Box>
              </MenuItem>
            );
          })}
        </TextField>

        {/* Aviso quando profissional compartilhado não tem permissão de agendamento */}
        {formData.professionalId && !selectedProfCanSchedule && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Você tem apenas acesso de <strong>Visualização</strong> nos agendamentos deste profissional. Para criar agendamentos é necessária permissão de <strong>Edição</strong>.
          </Alert>
        )}

        {/* Step 2: Service (filtrado pelo profissional) */}
        <TextField
          select
          fullWidth
          label="Serviço"
          value={formData.serviceId}
          onChange={(e) => handleChange('serviceId', e.target.value)}
          disabled={loading || !formData.professionalId}
          helperText={formData.professionalId && filteredServices.length === 0 ? 'Nenhum serviço disponível para este profissional' : ''}
          sx={{ mb: 2 }}
        >
          <MenuItem value=""><em>Selecione um serviço</em></MenuItem>
          {filteredServices.map((service) => (
            <MenuItem key={service.id} value={service.id}>
              {service.name}
            </MenuItem>
          ))}
        </TextField>

        {/* Step 3: Place (filtrado pelo profissional + serviço) */}
        <TextField
          select
          fullWidth
          label="Local"
          value={formData.placeId}
          onChange={(e) => handleChange('placeId', e.target.value)}
          disabled={loading || !formData.serviceId}
          helperText={formData.serviceId && filteredPlaces.length === 0 ? 'Este profissional não atende este serviço em nenhum local' : ''}
          sx={{ mb: 2 }}
        >
          <MenuItem value=""><em>Selecione um local</em></MenuItem>
          {filteredPlaces.map((place) => (
            <MenuItem key={place.id} value={place.id}>
              {place.name}
            </MenuItem>
          ))}
        </TextField>

        {/* Próximos horários disponíveis (aparece após selecionar prof + serviço + local) */}
        {formData.professionalId && formData.serviceId && formData.placeId && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              🚀 Próximos horários disponíveis (clique para selecionar):
            </Typography>
            {loadingNextSlots ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Buscando próximos horários...
                </Typography>
              </Box>
            ) : nextAvailableSlots.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {nextAvailableSlots.map((slot, index) => (
                  <Chip
                    key={index}
                    label={formatSlotDateTime(slot)}
                    onClick={() => handleNextSlotClick(slot)}
                    color="default"
                    variant="outlined"
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhum horário disponível nos próximos dias.
              </Typography>
            )}
          </Box>
        )}
      </>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon color="primary" />
          Novo Agendamento
        </Box>
        <IconButton onClick={handleClose} disabled={loading || loadingData} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Loading state enquanto carrega dados */}
        {loadingData && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }} color="text.secondary">
              Carregando dados...
            </Typography>
          </Box>
        )}

        {/* Alerta se não há dados */}
        {!loadingData && hasNoData && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
          >
            {professionals.length === 0 && places.length === 0 
              ? 'Você precisa criar um local com profissionais antes de agendar.'
              : professionals.length === 0 
                ? 'Nenhum profissional cadastrado.'
                : 'Nenhum local cadastrado.'
            }
          </Alert>
        )}

        {!loadingData && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Conteúdo do formulário - só mostra após carregar dados */}
        {!loadingData && !hasNoData && (
          <>

        <Box sx={{ mt: 2 }}>
          {/* Campos de seleção - sempre na mesma ordem */}
          {!hasNoData && renderSelectionFields()}

          {/* Date */}
          {formData.placeId && formData.serviceId && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                type="date"
                label="Data"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 
                  lang: 'pt-BR',
                  style: { colorScheme: 'light' }
                }}
              />
              <Button
                variant="contained"
                onClick={loadAvailableSlots}
                disabled={loading || loadingSlots || !formData.date}
                startIcon={loadingSlots ? <CircularProgress size={16} /> : <SearchIcon />}
                sx={{ minWidth: '140px', whiteSpace: 'nowrap' }}
              >
                {loadingSlots ? 'Buscando...' : 'Buscar Horários'}
              </Button>
            </Box>
          )}

          {/* Available Slots */}
          {formData.date && formData.serviceId && formData.placeId && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Horários Disponíveis
              </Typography>
              {loadingSlots ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : availableSlots.length === 0 ? (
                <Alert severity="info">
                  {availableSlots === null ? 'Clique em "Buscar Horários" para ver disponibilidade' : 'Nenhum horário disponível para esta data'}
                </Alert>
              ) : (
                <Grid container spacing={1}>
                  {availableSlots.map((slot, index) => (
                    <Grid item xs={4} sm={3} md={2} key={index}>
                      <Chip
                        label={formatSlotTime(slot)}
                        onClick={() => setFormData(prev => ({ ...prev, selectedSlot: slot }))}
                        color={
                          formData.selectedSlot?.startTime === slot.startTime
                            ? 'primary'
                            : 'default'
                        }
                        variant={
                          formData.selectedSlot?.startTime === slot.startTime
                            ? 'filled'
                            : 'outlined'
                        }
                        sx={{ width: '100%', cursor: 'pointer' }}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* Client Info */}
          {formData.selectedSlot && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Dados do Cliente
              </Typography>
              
              <TextField
                fullWidth
                label="Nome do Cliente *"
                value={formData.clientName}
                onChange={(e) => handleChange('clientName', e.target.value)}
                disabled={loading}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Telefone *"
                placeholder="(00) 00000-0000"
                value={formData.clientPhone}
                required
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, '');
                  if (value.length <= 10) {
                    value = value.replace(/^(\d{2})(\d{4})(\d{0,4}).*/, '($1) $2-$3');
                  } else {
                    value = value.replace(/^(\d{2})(\d{5})(\d{0,4}).*/, '($1) $2-$3');
                  }
                  handleChange('clientPhone', value.trim());
                }}
                disabled={loading}
                inputProps={{ maxLength: 15 }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="E-mail"
                type="email"
                value={formData.clientEmail}
                onChange={(e) => handleChange('clientEmail', e.target.value)}
                disabled={loading}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={3}
                value={formData.clientNotes}
                onChange={(e) => handleChange('clientNotes', e.target.value)}
                disabled={loading}
              />
            </>
          )}
        </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading || loadingData}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit || loadingData}
        >
          {loading ? <CircularProgress size={24} /> : 'Criar Agendamento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
